/**
 * JEFF SHOES — REVIEWS COMPONENT (BAYMARD INSTITUTE UX STANDARD)
 * High-performance, accessible, progressive review engine for Shopify.
 * Full support for: Customer Photos, Videos, and WhatsApp Voice Notes (Audio).
 */
(function () {
  'use strict';

  // Adaptive batch size: ensures "Cargar más" is interactive even on products with 3-5 reviews
  function getBatchSize(total) {
    if (total <= 2) return 2;
    if (total <= 5) return 2;
    if (total <= 8) return 3;
    return 5;
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function cdnUrl(u) {
    if (!u) return '';
    return String(u)
      .replace('https://revie-prod-images.s3.amazonaws.com/', 'https://revie-media.b-cdn.net/')
      .replace('https://media.revie.lat/', 'https://revie-media.b-cdn.net/');
  }

  function formatDate(isoString) {
    if (!isoString) return '';
    try {
      var parts = isoString.split('T')[0].split('-');
      if (parts.length === 3) {
        var months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
        var day = parseInt(parts[2], 10);
        var monthIdx = parseInt(parts[1], 10) - 1;
        var year = parts[0];
        return day + ' ' + (months[monthIdx] || '') + ' ' + year;
      }
    } catch (e) {}
    return String(isoString).split('T')[0];
  }

  function renderStarsHtml(rating, size) {
    var r = Math.max(0, Math.min(5, parseFloat(rating) || 0));
    var sz = size || 16;
    return '<div class="jeff-stars" style="--rvss: ' + sz + 'px; --rating: ' + r + ';" aria-label="' + r + ' de 5 estrellas">' +
      '<i class="rv-stars" style="--rvss: ' + sz + 'px; --rating: ' + r + '"></i>' +
      '</div>';
  }

  function getImages(rev) {
    if (!rev.images) return [];
    if (Array.isArray(rev.images)) return rev.images.filter(function (i) { return i && i.src && i.active !== false; });
    if (rev.images.src && rev.images.active !== false) return [rev.images];
    return [];
  }

  function getVideos(rev) {
    if (!rev.video) return [];
    if (Array.isArray(rev.video)) return rev.video.filter(function (v) { return v && v.src && v.active !== false; });
    if (rev.video.src && rev.video.active !== false) return [rev.video];
    return [];
  }

  function getAudios(rev) {
    if (!rev.audio) return [];
    if (Array.isArray(rev.audio)) return rev.audio.filter(function (a) { return a && a.src && a.active !== false; });
    if (rev.audio.src && rev.audio.active !== false) return [rev.audio];
    return [];
  }

  // Audio controller singleton
  var currentAudio = null;
  var currentAudioBox = null;

  function initReviewsEngine() {
    var root = document.getElementById('cont-rl-w') || document.querySelector('[data-rvw2]');
    if (!root) return;

    // Prevent Revie's legacy script from conflicting
    root.dataset.rvInit = 'jeff-active';

    var dataEl = root.querySelector('[data-rvw2-data]');
    var listEl = root.querySelector('[data-rvw2-list]');

    var rawData = {};
    var rawList = [];

    try { rawData = JSON.parse((dataEl && dataEl.textContent) || '{}') || {}; } catch (e) {}
    try { rawList = JSON.parse((listEl && listEl.textContent) || '[]') || []; } catch (e) {}

    // Calculate rating distribution and metrics
    var totalReviews = rawList.length || parseInt(rawData.reviewCount || '0', 10);
    var starCounts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    var ratingSum = 0;
    var totalPhotosCount = 0;
    var totalVideosCount = 0;
    var totalAudiosCount = 0;

    // Extract customer UGC media (photos & videos)
    var ugcMedia = [];

    rawList.forEach(function (rev, revIdx) {
      var s = Math.round(parseFloat(rev.stars) || 5);
      if (s >= 1 && s <= 5) {
        starCounts[s] = (starCounts[s] || 0) + 1;
      }
      ratingSum += (parseFloat(rev.stars) || 5);

      var imgs = getImages(rev);
      var vids = getVideos(rev);
      var auds = getAudios(rev);

      if (imgs.length > 0) totalPhotosCount += imgs.length;
      if (vids.length > 0) totalVideosCount += vids.length;
      if (auds.length > 0) totalAudiosCount += auds.length;

      var custName = (rev.customer && rev.customer.firstName) || 'Cliente';

      // Collect for UGC Reel
      vids.forEach(function (v) {
        ugcMedia.push({
          type: 'video',
          src: cdnUrl(v.src),
          customerName: custName,
          comment: rev.comment || '',
          rating: rev.stars || 5,
          reviewIdx: revIdx
        });
      });

      imgs.forEach(function (im) {
        ugcMedia.push({
          type: 'image',
          src: cdnUrl(im.src),
          customerName: custName,
          comment: rev.comment || '',
          rating: rev.stars || 5,
          reviewIdx: revIdx
        });
      });
    });

    var averageScore = 0;
    if (totalReviews > 0) {
      averageScore = rawData.reviewAverageValue ? parseFloat(rawData.reviewAverageValue) : (ratingSum / totalReviews);
      averageScore = Math.round(averageScore * 10) / 10;
    }

    // State management
    var state = {
      selectedStar: null,
      mediaFilter: null, // null, 'photo', 'video', 'audio'
      sort: 'recent',
      page: 1
    };

    function filterAndSortReviews() {
      var res = rawList.filter(function (rev) {
        if (state.selectedStar !== null) {
          var s = Math.round(parseFloat(rev.stars) || 5);
          if (s !== state.selectedStar) return false;
        }
        if (state.mediaFilter === 'photo') {
          if (getImages(rev).length === 0) return false;
        } else if (state.mediaFilter === 'video') {
          if (getVideos(rev).length === 0) return false;
        } else if (state.mediaFilter === 'audio') {
          if (getAudios(rev).length === 0) return false;
        }
        return true;
      });

      res.sort(function (a, b) {
        if (state.sort === 'top') {
          return (b.stars - a.stars) || String(b.createdAt).localeCompare(String(a.createdAt));
        }
        if (state.sort === 'low') {
          return (a.stars - b.stars) || String(b.createdAt).localeCompare(String(a.createdAt));
        }
        // Default: most recent
        return String(b.createdAt).localeCompare(String(a.createdAt));
      });

      return res;
    }

    // Create mount point
    var container = root.querySelector('#revieList');
    if (!container) {
      container = document.createElement('div');
      container.id = 'revieList';
      root.appendChild(container);
    }

    // Hide any legacy raw container
    var iaBox = root.querySelector('#revieDataIA');
    if (iaBox) iaBox.style.display = 'none';

    // RENDER A SINGLE REVIEW CARD
    function renderReviewCard(rev) {
      var customer = rev.customer || {};
      var name = (customer.firstName || 'Cliente') + (customer.lastName ? ' ' + customer.lastName.charAt(0) + '.' : '');
      var initial = (customer.firstName || 'C').charAt(0).toUpperCase();
      var comment = rev.comment ? rev.comment.trim() : '';
      var rating = parseFloat(rev.stars) || 5;
      var dateStr = formatDate(rev.createdAt);

      var imgs = getImages(rev);
      var vids = getVideos(rev);
      var auds = getAudios(rev);

      var cardHtml = '<article class="jeff-revie-card" data-review-item>';

      // Header
      cardHtml += '<div class="jeff-revie-card__header">';
      cardHtml += '<div class="jeff-revie-card__user">';
      cardHtml += '<span class="jeff-revie-card__avatar" aria-hidden="true">' + initial + '</span>';
      cardHtml += '<div class="jeff-revie-card__meta">';
      cardHtml += '<div class="jeff-revie-card__name-row">';
      cardHtml += '<strong class="jeff-revie-card__name">' + escapeHtml(name) + '</strong>';
      cardHtml += '<span class="jeff-revie-card__badge"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M10 3L4.5 8.5L2 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg> Comprador verificado</span>';
      cardHtml += '</div>';
      cardHtml += '<div class="jeff-revie-card__sub-row">';
      cardHtml += renderStarsHtml(rating, 13);
      cardHtml += '<span class="jeff-revie-card__date">' + dateStr + '</span>';
      cardHtml += '</div>';
      cardHtml += '</div>'; // End meta
      cardHtml += '</div>'; // End user
      cardHtml += '</div>'; // End header

      // Comment
      if (comment) {
        cardHtml += '<p class="jeff-revie-card__comment">' + escapeHtml(comment) + '</p>';
      }

      // NOTA DE VOZ (AUDIO WHATSAPP)
      if (auds.length > 0) {
        auds.forEach(function (aud) {
          var aSrc = cdnUrl(aud.src);
          cardHtml += '<div class="jeff-revie-audio" data-audio-src="' + escapeHtml(aSrc) + '">';
          cardHtml += '<button type="button" class="jeff-revie-audio__btn" data-audio-play aria-label="Reproducir nota de voz">▶</button>';
          cardHtml += '<div class="jeff-revie-audio__track">';
          cardHtml += '<div class="jeff-revie-audio__wave" data-wave></div>';
          cardHtml += '<div class="jeff-revie-audio__meta">';
          cardHtml += '<span class="jeff-revie-audio__dur" data-dur>0:00</span>';
          cardHtml += '<span class="jeff-revie-audio__tag">Nota de voz WhatsApp</span>';
          cardHtml += '</div>';
          cardHtml += '</div>'; // End track
          cardHtml += '</div>'; // End audio
        });
      }

      // MEDIA DE CLIENTES (FOTOS Y VIDEOS)
      if (imgs.length > 0 || vids.length > 0) {
        cardHtml += '<div class="jeff-revie-card__media-row">';

        // Videos
        vids.forEach(function (vid) {
          var vSrc = cdnUrl(vid.src);
          cardHtml += '<button type="button" class="jeff-revie-card__media-thumb is-video" data-open-video="' + escapeHtml(vSrc) + '" data-customer-name="' + escapeHtml(name) + '" aria-label="Reproducir video de ' + escapeHtml(name) + '">';
          cardHtml += '<video src="' + escapeHtml(vSrc) + '#t=0.01" preload="metadata" muted playsinline></video>';
          cardHtml += '<span class="jeff-revie-play-badge" aria-hidden="true">▶</span>';
          cardHtml += '</button>';
        });

        // Images
        imgs.forEach(function (img) {
          var cdnSrc = cdnUrl(img.src);
          cardHtml += '<button type="button" class="jeff-revie-card__media-thumb" data-open-media="' + escapeHtml(cdnSrc) + '" data-customer-name="' + escapeHtml(name) + '" aria-label="Ampliar foto de ' + escapeHtml(name) + '">';
          cardHtml += '<img src="' + escapeHtml(cdnSrc) + '?width=180&quality=85" alt="Foto de ' + escapeHtml(name) + '" loading="lazy" width="70" height="70">';
          cardHtml += '</button>';
        });

        cardHtml += '</div>';
      }

      // Merchant Response
      if (rev.commentResponse) {
        cardHtml += '<div class="jeff-revie-card__response">';
        cardHtml += '<strong class="jeff-revie-card__response-title">Respuesta de Jeff Shoes</strong>';
        cardHtml += '<p>' + escapeHtml(rev.commentResponse) + '</p>';
        cardHtml += '</div>';
      }

      cardHtml += '</article>';
      return cardHtml;
    }

    // Helper to generate waveforms
    function buildWaveformsIn(scope) {
      scope.querySelectorAll('[data-wave]').forEach(function (w, wIdx) {
        if (w.childNodes.length > 0) return;
        for (var i = 0; i < 28; i++) {
          var bar = document.createElement('i');
          var hPct = Math.abs(Math.sin((i + wIdx + 2) * 1.35) * Math.cos((i + wIdx) * 0.65)) * 100;
          bar.style.height = Math.max(18, Math.min(100, Math.round(hPct))) + '%';
          w.appendChild(bar);
        }
      });
    }

    // Bind audio in scope
    function bindAudioIn(scope) {
      scope.querySelectorAll('[data-audio-play]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var box = btn.closest('[data-audio-src]');
          if (!box) return;
          var src = box.dataset.audioSrc;
          if (!src) return;

          // Toggle if already playing this audio
          if (currentAudio && currentAudioBox === box) {
            if (currentAudio.paused) {
              currentAudio.play();
              box.classList.add('is-playing');
              btn.textContent = '❚❚';
            } else {
              currentAudio.pause();
              box.classList.remove('is-playing');
              btn.textContent = '▶';
            }
            return;
          }

          // Stop previous audio
          if (currentAudio) {
            currentAudio.pause();
            if (currentAudioBox) {
              currentAudioBox.classList.remove('is-playing');
              var prevBtn = currentAudioBox.querySelector('[data-audio-play]');
              if (prevBtn) prevBtn.textContent = '▶';
            }
          }

          // Initialize new audio
          currentAudioBox = box;
          currentAudio = new Audio(src);
          box.classList.add('is-playing');
          btn.textContent = '❚❚';

          var durEl = box.querySelector('[data-dur]');

          currentAudio.addEventListener('loadedmetadata', function () {
            if (durEl && isFinite(currentAudio.duration)) {
              var s = Math.round(currentAudio.duration);
              durEl.textContent = Math.floor(s / 60) + ':' + ('0' + (s % 60)).slice(-2);
            }
          });

          currentAudio.addEventListener('timeupdate', function () {
            if (durEl && isFinite(currentAudio.currentTime)) {
              var s = Math.round(currentAudio.currentTime);
              durEl.textContent = Math.floor(s / 60) + ':' + ('0' + (s % 60)).slice(-2);
            }
          });

          currentAudio.addEventListener('ended', function () {
            box.classList.remove('is-playing');
            btn.textContent = '▶';
          });

          currentAudio.play().catch(function (e) {
            box.classList.remove('is-playing');
            btn.textContent = '▶';
          });
        });
      });
    }

    // Lightbox modal variables
    var lbModal = null;
    var lbContent = null;
    var lbCaption = null;
    var lbClose = null;
    var lbPrevious = null;
    var lbNext = null;
    var lightboxItems = [];
    var activeMediaIndex = 0;
    var lastLightboxTrigger = null;

    function updateLightbox(index) {
      var item = lightboxItems[index];
      if (!lbModal || !lbContent) return;
      lbContent.innerHTML = '';
      if (!item) return;

      activeMediaIndex = index;

      if (item.type === 'video') {
        var videoEl = document.createElement('video');
        videoEl.src = item.src;
        videoEl.controls = true;
        videoEl.autoplay = true;
        videoEl.playsInline = true;
        videoEl.setAttribute('aria-label', 'Video compartido por ' + (item.customerName || 'un cliente'));
        videoEl.className = 'jeff-revie-modal__video';
        lbContent.appendChild(videoEl);
      } else {
        var imgEl = document.createElement('img');
        imgEl.src = item.src;
        imgEl.alt = 'Foto compartida por ' + (item.customerName || 'un cliente');
        imgEl.className = 'jeff-revie-modal__image';
        lbContent.appendChild(imgEl);
      }

      if (lbCaption) {
        var mediaLabel = item.type === 'video' ? 'Video' : 'Foto';
        var positionLabel = lightboxItems.length > 1 ? ' · ' + (index + 1) + ' de ' + lightboxItems.length : '';
        lbCaption.textContent = mediaLabel + ' de ' + (item.customerName || 'un cliente') + positionLabel;
      }
      if (lbPrevious) {
        lbPrevious.hidden = lightboxItems.length < 2;
        lbPrevious.style.display = lightboxItems.length < 2 ? 'none' : 'inline-flex';
      }
      if (lbNext) {
        lbNext.hidden = lightboxItems.length < 2;
        lbNext.style.display = lightboxItems.length < 2 ? 'none' : 'inline-flex';
      }
    }

    function openLightbox(items, index, trigger) {
      if (!lbModal || !lbContent || !items.length) return;
      lightboxItems = items;
      lastLightboxTrigger = trigger || null;
      updateLightbox(index || 0);
      document.body.classList.add('jeff-lightbox-open');
      if (!lbModal.open) lbModal.showModal();
      if (lbClose) lbClose.focus();
    }

    function closeLightbox() {
      if (!lbModal || !lbContent) return;
      var activeVideo = lbContent.querySelector('video');
      if (activeVideo) activeVideo.pause();
      if (lbModal.open) lbModal.close();
    }

    function resetLightbox() {
      if (lbContent) lbContent.innerHTML = '';
      if (lbCaption) lbCaption.textContent = '';
      document.body.classList.remove('jeff-lightbox-open');
      if (lastLightboxTrigger && document.contains(lastLightboxTrigger)) {
        lastLightboxTrigger.focus();
      }
      lastLightboxTrigger = null;
      lightboxItems = [];
    }

    function moveLightbox(direction) {
      if (lightboxItems.length < 2) return;
      var nextIndex = (activeMediaIndex + direction + lightboxItems.length) % lightboxItems.length;
      updateLightbox(nextIndex);
    }

    // Bind media clicks on review cards
    function bindCardMediaIn(scope) {
      scope.querySelectorAll('[data-open-media]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var src = btn.dataset.openMedia;
          var foundIdx = -1;
          for (var i = 0; i < ugcMedia.length; i++) {
            if (ugcMedia[i].src === src) { foundIdx = i; break; }
          }
          if (foundIdx !== -1) {
            openLightbox(ugcMedia, foundIdx, btn);
          } else {
            openLightbox([{ type: 'image', src: src, customerName: btn.dataset.customerName }], 0, btn);
          }
        });
      });

      scope.querySelectorAll('[data-open-video]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var src = btn.dataset.openVideo;
          var foundIdx = -1;
          for (var i = 0; i < ugcMedia.length; i++) {
            if (ugcMedia[i].src === src) { foundIdx = i; break; }
          }
          if (foundIdx !== -1) {
            openLightbox(ugcMedia, foundIdx, btn);
          } else {
            openLightbox([{ type: 'video', src: src, customerName: btn.dataset.customerName }], 0, btn);
          }
        });
      });
    }

    // RENDER MAIN COMPONENT
    function renderMain() {
      if (totalReviews === 0) {
        container.innerHTML = '<div class="jeff-revie-empty">' +
          '<div class="jeff-revie-empty__stars">' + renderStarsHtml(5, 20) + '</div>' +
          '<h3 class="jeff-revie-empty__title">Sé el primero en calificar este modelo</h3>' +
          '<p class="jeff-revie-empty__text">Tus comentarios nos ayudan a seguir creando calzado con el mejor confort y diseño artesanal.</p>' +
          '</div>';
        return;
      }

      var filtered = filterAndSortReviews();
      var totalFiltered = filtered.length;
      var pageSize = getBatchSize(totalFiltered);
      var visibleReviews = filtered.slice(0, state.page * pageSize);
      var hasMore = totalFiltered > visibleReviews.length;
      var filterAnnouncement = state.selectedStar !== null
        ? totalFiltered + ' opiniones con ' + state.selectedStar + (state.selectedStar === 1 ? ' estrella.' : ' estrellas.')
        : totalFiltered + (totalFiltered === 1 ? ' opinión.' : ' opiniones.');

      var html = '<section class="jeff-revie" aria-labelledby="jeff-revie-heading">';
      html += '<h2 id="jeff-revie-heading" class="jeff-revie__section-title visually-hidden">Opiniones de Clientes</h2>';

      // 1. SCORE SUMMARY & BAYMARD INTERACTIVE BREAKDOWN
      html += '<div class="jeff-revie-summary">';

      // Col 1: Average Score
      html += '<div class="jeff-revie-score-box">';
      html += '<span class="jeff-revie-score-num">' + averageScore.toFixed(1) + '</span>';
      html += '<div class="jeff-revie-score-stars">' + renderStarsHtml(averageScore, 20) + '</div>';
      html += '<span class="jeff-revie-score-count">Basado en ' + totalReviews + ' opiniones</span>';
      html += '</div>';

      // Col 2: Interactive Breakdown Bars
      html += '<div class="jeff-revie-breakdown" role="region" aria-label="Desglose interactivo de calificaciones">';
      [5, 4, 3, 2, 1].forEach(function (starTier) {
        var count = starCounts[starTier] || 0;
        var pct = totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0;
        var fillWidth = count > 0 ? Math.max(2, pct) : 0;
        var isActive = state.selectedStar === starTier;

        html += '<button type="button" class="jeff-rating-bar' + (isActive ? ' is-active' : '') + '" data-star-filter="' + starTier + '" aria-pressed="' + isActive + '">';
        html += '<span class="jeff-rating-bar__label">' + starTier + ' ★</span>';
        html += '<div class="jeff-rating-bar__track" style="--jeff-bar-fill: ' + fillWidth + '%;"><div class="jeff-rating-bar__fill" style="width: ' + fillWidth + '%;"></div></div>';
        html += '<span class="jeff-rating-bar__count">' + count + '</span>';
        html += '</button>';
      });
      html += '</div>';

      html += '</div>'; // End summary

      // 2. UGC MEDIA REEL (FOTOS Y VIDEOS - GRID RESPONSIVE SIN SCROLL)
      if (ugcMedia.length > 0) {
        html += '<div class="jeff-revie-ugc">';
        html += '<div class="jeff-revie-ugc__header">';
        html += '<span class="jeff-revie-ugc__title">Fotos y videos de clientes (' + ugcMedia.length + ')</span>';
        if (ugcMedia.length > 4) {
          html += '<button type="button" class="jeff-revie-ugc__see-all" data-open-gallery>Ver galería completa</button>';
        }
        html += '</div>';
        html += '<div class="jeff-revie-ugc__grid">';

        var displayLimit = Math.min(6, ugcMedia.length);
        for (var mIdx = 0; mIdx < displayLimit; mIdx++) {
          var med = ugcMedia[mIdx];
          var isVideo = med.type === 'video';

          html += '<button type="button" class="jeff-revie-ugc__thumb' + (isVideo ? ' is-video' : '') + '" data-lightbox-idx="' + mIdx + '" aria-label="Ver ' + (isVideo ? 'video' : 'foto') + ' de ' + escapeHtml(med.customerName) + '">';
          if (isVideo) {
            html += '<video src="' + escapeHtml(med.src) + '#t=0.01" preload="metadata" muted playsinline></video>';
            html += '<span class="jeff-revie-play-badge" aria-hidden="true">▶</span>';
          } else {
            html += '<img src="' + escapeHtml(med.src) + '?width=200&quality=85" alt="Foto de ' + escapeHtml(med.customerName) + '" loading="lazy" width="80" height="80">';
          }

          if (mIdx === 3 && ugcMedia.length > 4) {
            html += '<span class="jeff-revie-ugc__more jeff-revie-ugc__more--mobile">+' + (ugcMedia.length - 3) + '</span>';
          }
          if (mIdx === 5 && ugcMedia.length > 6) {
            html += '<span class="jeff-revie-ugc__more jeff-revie-ugc__more--desktop">+' + (ugcMedia.length - 5) + '</span>';
          }

          html += '</button>';
        }
        html += '</div>';
        html += '</div>';
      }

      // 3. CONTROLS BAR (CHIPS DE FILTROS & ORDENAMIENTO)
      html += '<div class="jeff-revie-bar">';
      html += '<div class="jeff-revie-chips" role="group" aria-label="Filtros de opiniones">';

      var isAllActive = state.selectedStar === null && state.mediaFilter === null;
      html += '<button type="button" class="jeff-revie-chip' + (isAllActive ? ' is-active' : '') + '" data-filter-chip="all">Todas (' + totalReviews + ')</button>';

      if (totalPhotosCount > 0) {
        var isPhotoActive = state.mediaFilter === 'photo';
        html += '<button type="button" class="jeff-revie-chip' + (isPhotoActive ? ' is-active' : '') + '" data-filter-chip="photo">📷 Con foto (' + totalPhotosCount + ')</button>';
      }

      if (totalVideosCount > 0) {
        var isVideoActive = state.mediaFilter === 'video';
        html += '<button type="button" class="jeff-revie-chip' + (isVideoActive ? ' is-active' : '') + '" data-filter-chip="video">🎥 Con video (' + totalVideosCount + ')</button>';
      }

      if (totalAudiosCount > 0) {
        var isAudioActive = state.mediaFilter === 'audio';
        html += '<button type="button" class="jeff-revie-chip' + (isAudioActive ? ' is-active' : '') + '" data-filter-chip="audio">🎙️ Con audio (' + totalAudiosCount + ')</button>';
      }

      if (state.selectedStar !== null) {
        html += '<span class="jeff-revie-active-pill">' + state.selectedStar + ' Estrellas <button type="button" data-clear-star aria-label="Quitar filtro de estrellas">&times;</button></span>';
      }

      html += '</div>';

      // Sort Select
      html += '<div class="jeff-revie-sort">';
      html += '<label for="jeff-revie-sort-select" class="visually-hidden">Ordenar opiniones</label>';
      html += '<select id="jeff-revie-sort-select" class="jeff-revie-select" data-sort-select>';
      html += '<option value="recent"' + (state.sort === 'recent' ? ' selected' : '') + '>Más recientes</option>';
      html += '<option value="top"' + (state.sort === 'top' ? ' selected' : '') + '>Mejor valoradas</option>';
      html += '<option value="low"' + (state.sort === 'low' ? ' selected' : '') + '>Menor puntuación</option>';
      html += '</select>';
      html += '</div>';

      html += '</div>';

      // 4. LISTA DE RESEÑAS
      html += '<div class="jeff-revie-list">';
      if (visibleReviews.length === 0) {
        html += '<div class="jeff-revie-no-matches"><p>No se encontraron opiniones con los filtros seleccionados.</p><button type="button" class="jeff-revie-chip is-active" data-filter-chip="all">Restablecer filtros</button></div>';
      } else {
        visibleReviews.forEach(function (rev) {
          html += renderReviewCard(rev);
        });
      }
      html += '</div>';
      html += '<p class="visually-hidden" role="status" aria-live="polite" data-review-filter-status>' + filterAnnouncement + '</p>';

      // 5. BAYMARD GOLD STANDARD "LOAD MORE" CONTROLLER
      if (totalFiltered > 0) {
        var pctLoaded = Math.min(100, Math.round((visibleReviews.length / totalFiltered) * 100));

        html += '<div class="jeff-revie-load-area" data-load-area>';
        html += '<div class="jeff-revie-progress">';
        html += '<span class="jeff-revie-progress__text">Mostrando <strong>' + visibleReviews.length + '</strong> de <strong>' + totalFiltered + '</strong> opiniones</span>';
        html += '<div class="jeff-revie-progress__bar" style="--jeff-bar-fill: ' + pctLoaded + '%;" role="progressbar" aria-valuenow="' + visibleReviews.length + '" aria-valuemin="0" aria-valuemax="' + totalFiltered + '">';
        html += '<div class="jeff-revie-progress__fill" style="width: ' + pctLoaded + '%;"></div>';
        html += '</div>';
        html += '</div>';

        if (hasMore) {
          html += '<button type="button" class="jeff-revie-load-btn" data-action-load-more>';
          html += '<span class="jeff-revie-load-btn__label">Cargar más opiniones</span>';
          html += '<span class="jeff-revie-load-btn__spinner" aria-hidden="true"></span>';
          html += '</button>';
        } else {
          html += '<p class="jeff-revie-all-loaded"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M11.6666 3.5L5.24992 9.91667L2.33325 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg> Has visto todas las opiniones (' + totalFiltered + ')</p>';
        }
        html += '</div>';
      }

      html += '</section>';

      // Lightbox Dialog
      html += '<dialog id="jeff-revie-lightbox" class="jeff-revie-modal" aria-labelledby="jeff-revie-lightbox-title">';
      html += '<div class="jeff-revie-modal__box">';
      html += '<button type="button" class="jeff-revie-modal__close" data-close-lightbox aria-label="Cerrar foto o video" title="Cerrar">';
      html += '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg><span class="visually-hidden">Cerrar</span></button>';
      html += '<button type="button" class="jeff-revie-modal__nav jeff-revie-modal__nav--previous" data-lightbox-previous aria-label="Ver archivo anterior">';
      html += '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14.5 5-7 7 7 7"/></svg></button>';
      html += '<div class="jeff-revie-modal__content" data-lightbox-content></div>';
      html += '<button type="button" class="jeff-revie-modal__nav jeff-revie-modal__nav--next" data-lightbox-next aria-label="Ver archivo siguiente">';
      html += '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9.5 5 7 7-7 7"/></svg></button>';
      html += '<p id="jeff-revie-lightbox-title" class="jeff-revie-modal__caption" data-lightbox-caption></p>';
      html += '</div>';
      html += '</dialog>';

      container.innerHTML = html;
      bindEvents();
      buildWaveformsIn(container);
    }

    // EVENT DELEGATION & USER INTERACTIONS
    function bindEvents() {
      // Star Breakdown Click
      container.querySelectorAll('[data-star-filter]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var star = parseInt(btn.dataset.starFilter, 10);
          state.selectedStar = state.selectedStar === star ? null : star;
          state.page = 1;
          renderMain();
          requestAnimationFrame(function () {
            var target = container.querySelector('.jeff-revie-bar');
            if (!target) return;

            var stickyHeader = document.querySelector('.shopify-section-header-sticky');
            var headerBottom = stickyHeader ? Math.max(0, stickyHeader.getBoundingClientRect().bottom) : 0;
            var top = window.scrollY + target.getBoundingClientRect().top - headerBottom - 12;
            var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
          });
        });
      });

      // Clear Star Pill
      var clearStarBtn = container.querySelector('[data-clear-star]');
      if (clearStarBtn) {
        clearStarBtn.addEventListener('click', function () {
          state.selectedStar = null;
          state.page = 1;
          renderMain();
        });
      }

      // Filter Chips
      container.querySelectorAll('[data-filter-chip]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var chip = btn.dataset.filterChip;
          if (chip === 'all') {
            state.selectedStar = null;
            state.mediaFilter = null;
          } else {
            state.mediaFilter = state.mediaFilter === chip ? null : chip;
          }
          state.page = 1;
          renderMain();
        });
      });

      // Sort Select
      var sortSelect = container.querySelector('[data-sort-select]');
      if (sortSelect) {
        sortSelect.addEventListener('change', function () {
          state.sort = sortSelect.value;
          state.page = 1;
          renderMain();
        });
      }

      // "CARGAR MÁS" BUTTON ACTION (IN-PLACE APPENDING & DYNAMIC SMOOTH BAR FILL)
      var loadMoreBtn = container.querySelector('[data-action-load-more]');
      if (loadMoreBtn) {
        loadMoreBtn.addEventListener('click', function () {
          if (loadMoreBtn.classList.contains('is-loading')) return;
          loadMoreBtn.classList.add('is-loading');

          setTimeout(function () {
            var filtered = filterAndSortReviews();
            var totalFiltered = filtered.length;
            var pageSize = getBatchSize(totalFiltered);
            var currentLoaded = state.page * pageSize;
            var nextBatch = filtered.slice(currentLoaded, currentLoaded + pageSize);

            if (nextBatch.length > 0) {
              state.page++;
              var newLoaded = Math.min(totalFiltered, currentLoaded + nextBatch.length);

              var listElement = container.querySelector('.jeff-revie-list');
              if (listElement) {
                var newCardsHtml = '';
                nextBatch.forEach(function (rev) {
                  newCardsHtml += renderReviewCard(rev);
                });

                var tempDiv = document.createElement('div');
                tempDiv.innerHTML = newCardsHtml;
                var appendedNodes = [];
                while (tempDiv.firstChild) {
                  var child = tempDiv.firstChild;
                  listElement.appendChild(child);
                  if (child.nodeType === 1) appendedNodes.push(child);
                }

                appendedNodes.forEach(function (node) {
                  bindAudioIn(node);
                  bindCardMediaIn(node);
                  buildWaveformsIn(node);
                });
              }

              // Update progress text dynamically
              var progressText = container.querySelector('.jeff-revie-progress__text');
              if (progressText) {
                progressText.innerHTML = 'Mostrando <strong>' + newLoaded + '</strong> de <strong>' + totalFiltered + '</strong> opiniones';
              }

              // Update progress bar dynamically with smooth CSS transition
              var progressFill = container.querySelector('.jeff-revie-progress__fill');
              var progressBar = container.querySelector('.jeff-revie-progress__bar');
              var newPct = Math.min(100, Math.round((newLoaded / totalFiltered) * 100));
              if (progressFill) {
                progressFill.style.width = newPct + '%';
              }
              if (progressBar) {
                progressBar.style.setProperty('--jeff-bar-fill', newPct + '%');
                progressBar.setAttribute('aria-valuenow', newLoaded);
              }

              // If finished all reviews
              if (newLoaded >= totalFiltered) {
                var loadArea = container.querySelector('[data-load-area]');
                if (loadArea) {
                  loadMoreBtn.remove();
                  var doneP = document.createElement('p');
                  doneP.className = 'jeff-revie-all-loaded';
                  doneP.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M11.6666 3.5L5.24992 9.91667L2.33325 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg> Has visto todas las opiniones (' + totalFiltered + ')';
                  loadArea.appendChild(doneP);
                }
              } else {
                loadMoreBtn.classList.remove('is-loading');
              }

              var announcement = document.createElement('div');
              announcement.setAttribute('role', 'status');
              announcement.setAttribute('aria-live', 'polite');
              announcement.className = 'visually-hidden';
              announcement.textContent = 'Se cargaron más opiniones. Mostrando ' + newLoaded + ' de ' + totalFiltered + '.';
              document.body.appendChild(announcement);
              setTimeout(function () { announcement.remove(); }, 1500);
            } else {
              loadMoreBtn.classList.remove('is-loading');
            }
          }, 200);
        });
      }

      // AUDIO CONTROLLER
      bindAudioIn(container);

      // CARD MEDIA CLICKS
      bindCardMediaIn(container);

      // LIGHTBOX SETUP
      lbModal = container.querySelector('#jeff-revie-lightbox');
      lbContent = container.querySelector('[data-lightbox-content]');
      lbCaption = container.querySelector('[data-lightbox-caption]');
      lbClose = container.querySelector('[data-close-lightbox]');
      lbPrevious = container.querySelector('[data-lightbox-previous]');
      lbNext = container.querySelector('[data-lightbox-next]');

      // UGC Reel click
      container.querySelectorAll('[data-lightbox-idx]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var idx = parseInt(thumb.dataset.lightboxIdx, 10);
          if (ugcMedia[idx]) {
            openLightbox(ugcMedia, idx, thumb);
          }
        });
      });

      // "Ver galería completa" button
      var seeAllGalleryBtn = container.querySelector('[data-open-gallery]');
      if (seeAllGalleryBtn) {
        seeAllGalleryBtn.addEventListener('click', function () {
          openLightbox(ugcMedia, 0, seeAllGalleryBtn);
        });
      }

      var lastNavTime = 0;
      function handleNav(direction, e) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        var now = Date.now();
        if (now - lastNavTime < 200) return;
        lastNavTime = now;
        moveLightbox(direction);
      }

      container.querySelectorAll('[data-close-lightbox]').forEach(function (el) {
        el.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          closeLightbox();
        });
        el.addEventListener('touchend', function (e) {
          e.preventDefault();
          e.stopPropagation();
          closeLightbox();
        });
      });

      if (lbPrevious) {
        lbPrevious.addEventListener('click', function (e) { handleNav(-1, e); });
        lbPrevious.addEventListener('touchend', function (e) { handleNav(-1, e); });
      }
      if (lbNext) {
        lbNext.addEventListener('click', function (e) { handleNav(1, e); });
        lbNext.addEventListener('touchend', function (e) { handleNav(1, e); });
      }

      if (lbModal) {
        lbModal.addEventListener('click', function (event) {
          if (event.target === lbModal || (event.target && event.target.classList.contains('jeff-revie-modal__box'))) {
            closeLightbox();
          }
        });
        lbModal.addEventListener('close', resetLightbox);
        lbModal.addEventListener('keydown', function (event) {
          if (event.key === 'ArrowLeft') moveLightbox(-1);
          if (event.key === 'ArrowRight') moveLightbox(1);
          if (event.key === 'Escape') closeLightbox();
        });

        // Touch Swipe gestures anywhere on the Lightbox modal
        var touchStartX = 0;
        var touchStartY = 0;
        lbModal.addEventListener('touchstart', function (e) {
          if (e.target && e.target.closest('[data-lightbox-previous], [data-lightbox-next], [data-close-lightbox]')) return;
          if (e.touches && e.touches.length === 1) {
            touchStartX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
          }
        }, { passive: true });

        lbModal.addEventListener('touchend', function (e) {
          if (e.target && e.target.closest('[data-lightbox-previous], [data-lightbox-next], [data-close-lightbox]')) return;
          if (e.changedTouches && e.changedTouches.length === 1) {
            var diffX = e.changedTouches[0].clientX - touchStartX;
            var diffY = e.changedTouches[0].clientY - touchStartY;
            if (Math.abs(diffX) > 35 && Math.abs(diffX) > Math.abs(diffY)) {
              if (diffX < 0) {
                handleNav(1, e);
              } else {
                handleNav(-1, e);
              }
            }
          }
        }, { passive: true });
      }
    }

    renderMain();
  }

  // PROGRESSIVE / IDLE BOOTSTRAP (Prevents blocking PDP Main Thread & improves INP)
  var isReviewsInitialized = false;
  function safeInitReviewsEngine() {
    if (isReviewsInitialized) return;
    isReviewsInitialized = true;
    initReviewsEngine();
  }

  function scheduleInit() {
    var root = document.getElementById('cont-rl-w') || document.querySelector('[data-rvw2]');
    if (!root) return;

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        if (entries[0] && entries[0].isIntersecting) {
          observer.disconnect();
          safeInitReviewsEngine();
        }
      }, { rootMargin: '600px 0px' });
      observer.observe(root);
    }

    // Fallback: Idle callback so it always initializes without blocking critical user interaction
    if ('requestIdleCallback' in window) {
      requestIdleCallback(safeInitReviewsEngine, { timeout: 3500 });
    } else {
      setTimeout(safeInitReviewsEngine, 2000);
    }

    // Fallback on user scroll or touch
    window.addEventListener('scroll', safeInitReviewsEngine, { passive: true, once: true });
    window.addEventListener('touchstart', safeInitReviewsEngine, { passive: true, once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInit);
  } else {
    scheduleInit();
  }

  document.addEventListener('shopify:section:load', safeInitReviewsEngine);
})();
